import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Rating,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { coerceBool } from "../../utils/utils";
import {
  useLazyGetPreSignedUrlQuery,
  useUpdateItemMutation,
  useGetItemByIdQuery,
} from "../../services/items/items";
import { uploadImage } from "../../utils/uploadImage";
import { resolveAssetUrl } from "../../utils/assets";
import { getErrorStatus, HTTP_NOT_FOUND } from "../../utils/apiError";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import SaveIcon from "@mui/icons-material/Save";
import { CelularSpecs, ItemType, ProductRelation } from "../../types/types";
import RelatedProductsEditor from "./RelatedProductsEditor";
import CelularSpecsEditor, { EMPTY_SPECS } from "./CelularSpecsEditor";
import { onlyNumbers, toStringSafe } from "../shared/utils";
import {
  CATEGORY_LABELS,
  DISCOUNT_RANGE_ERROR,
  INPUT_LABEL_PROPS,
  ITEM_MAINTENANCE_TEXTS,
  FORM_LABELS,
  IMAGE_BORDER_RADIUS,
} from "../shared/constants";
import {
  CommonBoxStyles,
  ImageUploadBar,
  SaveActionBar,
  FormFieldGrid,
  FormFieldGridResponsive,
} from "../shared/styledComponents";

// Re-export for backward compatibility
export { CommonBoxStyles } from "../shared/styledComponents";

type Props = {
  id: string;
};

type FormState = {
  available_quantity: string;
  lead_time_days: string;
  description: string;
  discount: string;
  is_active: boolean;
  name: string;
  picture: string;
  price: string;
  images: string[];
  tipo_producto: string;
  marca: string;
  modelo: string;
  variante: string;
  condicion: string;
  garantia_meses: string;
  precio_referencia_cop: string;
  url_oficial: string;
};

const ItemMaintenanceForm: React.FC<Props> = ({ id }) => {
  const {
    data: item,
    refetch,
    isFetching,
    isError,
  } = useGetItemByIdQuery(id, {
    skip: !id,
  });

  const [getPreSignedUrl] = useLazyGetPreSignedUrlQuery();
  const [updateItem, { isLoading: isSaving }] = useUpdateItemMutation();

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const initialForm: FormState = useMemo(() => {
    const src: ItemType = (item as ItemType) ?? ({} as ItemType);
    const images: string[] = Array.isArray(src.images)
      ? src.images
      : Array.isArray(src.images)
        ? src.images
        : [];

    return {
      available_quantity: toStringSafe(src.available_quantity),
      lead_time_days: toStringSafe(src.lead_time_days),
      description: src.description ?? "",
      discount: toStringSafe(src.discount),
      is_active: coerceBool(src.is_active),
      name: src.name ?? "",
      picture: src.picture ?? "",
      price: toStringSafe(src.price),
      images,
      tipo_producto: src.tipo_producto ?? "",
      marca: src.marca ?? "",
      modelo: src.modelo ?? "",
      variante: src.variante ?? "",
      condicion: src.condicion ?? "",
      garantia_meses: toStringSafe(src.garantia_meses),
      precio_referencia_cop: toStringSafe(src.precio_referencia_cop),
      url_oficial: src.url_oficial ?? "",
    };
  }, [item]);

  const [availableQuantity, setAvailableQuantity] = useState<string>(
    initialForm.available_quantity,
  );
  const [leadTimeDays, setLeadTimeDays] = useState<string>(initialForm.lead_time_days);
  const [description, setDescription] = useState<string>(initialForm.description);
  const [discount, setDiscount] = useState<string>(initialForm.discount);
  const [isActive, setIsActive] = useState<boolean>(initialForm.is_active);
  const [name, setName] = useState<string>(initialForm.name);
  const [pictureUrl, setPictureUrl] = useState<string>(initialForm.picture);
  const [price, setPrice] = useState<string>(initialForm.price);
  const [images, setImages] = useState<string[]>(initialForm.images);
  const [tipoProducto, setTipoProducto] = useState<string>(initialForm.tipo_producto);
  const [marca, setMarca] = useState<string>(initialForm.marca);
  const [modelo, setModelo] = useState<string>(initialForm.modelo);
  const [variante, setVariante] = useState<string>(initialForm.variante);
  const [condicion, setCondicion] = useState<string>(initialForm.condicion);
  const [garantiaMeses, setGarantiaMeses] = useState<string>(initialForm.garantia_meses);
  const [precioReferenciaCop, setPrecioReferenciaCop] = useState<string>(
    initialForm.precio_referencia_cop,
  );
  const [urlOficial, setUrlOficial] = useState<string>(initialForm.url_oficial);
  const [specs, setSpecs] = useState<CelularSpecs>(
    ((item as ItemType)?.specs as CelularSpecs) ?? { ...EMPTY_SPECS },
  );
  const [productosRelacionados, setProductosRelacionados] = useState<ProductRelation[]>(
    (item as ItemType)?.productos_relacionados ?? [],
  );
  const [rating, setRating] = useState<number | null>((item as ItemType)?.rating ?? null);

  // Read straight off the fetched item, with no state behind it — that absence is the point.
  // `fuente` has no setter anywhere in the admin (EB-524) because the sync owns it.
  const fuente = (item as ItemType)?.fuente ?? [];

  useEffect(() => {
    setAvailableQuantity(initialForm.available_quantity);
    setLeadTimeDays(initialForm.lead_time_days);
    setDescription(initialForm.description);
    setDiscount(initialForm.discount);
    setIsActive(initialForm.is_active);
    setName(initialForm.name);
    setPictureUrl(initialForm.picture);
    setPrice(initialForm.price);
    setImages(initialForm.images);
    setTipoProducto(initialForm.tipo_producto);
    setMarca(initialForm.marca);
    setModelo(initialForm.modelo);
    setVariante(initialForm.variante);
    setCondicion(initialForm.condicion);
    setGarantiaMeses(initialForm.garantia_meses);
    setPrecioReferenciaCop(initialForm.precio_referencia_cop);
    setUrlOficial(initialForm.url_oficial);
    setSpecs(((item as ItemType)?.specs as CelularSpecs) ?? { ...EMPTY_SPECS });
    setProductosRelacionados((item as ItemType)?.productos_relacionados ?? []);
    setRating((item as ItemType)?.rating ?? null);
  }, [initialForm, item]);

  const imageMainInputRef = useRef<HTMLInputElement | null>(null);
  const imageGalleryInputRef = useRef<HTMLInputElement | null>(null);

  const openMainPicker = () => {
    setErrorMsg(null);
    imageMainInputRef.current?.click();
  };

  const openGalleryPicker = () => {
    setErrorMsg(null);
    imageGalleryInputRef.current?.click();
  };

  const handleMainSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setErrorMsg(null);
      const file = e.target.files?.[0];
      if (!file) return;

      setUploading(true);

      const presigned = await getPreSignedUrl({ id, _ts: Date.now() }).unwrap();
      await uploadImage(file, presigned.uploadUrl, presigned.headers["Content-Type"]);

      // The row stores the key (EB-515); the preview resolves it exactly as the storefront will,
      // so what an admin sees after uploading is the same URL a shopper gets.
      setPreviewUrl(presigned.key);
      setPictureUrl(presigned.key);

      await updateItem({ id, patch: { picture: presigned.key } }).unwrap();
      await refetch();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Error subiendo la imagen.");
    } finally {
      setUploading(false);
      if (imageMainInputRef.current) imageMainInputRef.current.value = "";
    }
  };

  const handleGallerySelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setErrorMsg(null);
      const file = e.target.files?.[0];
      if (!file) return;

      setUploading(true);

      const presigned = await getPreSignedUrl({ id, _ts: Date.now() }).unwrap();
      await uploadImage(file, presigned.uploadUrl, presigned.headers["Content-Type"]);

      setPreviewUrl(presigned.key);

      const nextImages = [...images, presigned.key];
      setImages(nextImages);

      await updateItem({ id, patch: { images: nextImages } }).unwrap();
      await refetch();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Error subiendo la imagen.");
    } finally {
      setUploading(false);
      if (imageGalleryInputRef.current) imageGalleryInputRef.current.value = "";
    }
  };

  const removeImageAt = async (idx: number): Promise<void> => {
    const next = images.filter((_, i) => i !== idx);
    setImages(next);
    try {
      await updateItem({ id, patch: { images: next } }).unwrap();
      await refetch();
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.error("Update failed:", err);
      }
    }
  };

  const handleSave = async (): Promise<void> => {
    setErrorMsg(null);

    const priceNumber = price ? Number(price) : undefined;
    const discountNumber = discount ? Number(discount) : undefined;

    if ((price && Number.isNaN(priceNumber)) || (discount && Number.isNaN(discountNumber))) {
      setErrorMsg(ITEM_MAINTENANCE_TEXTS.numericError);
      return;
    }

    if (discountNumber !== undefined && (discountNumber < 0 || discountNumber > 100)) {
      setErrorMsg(DISCOUNT_RANGE_ERROR);
      return;
    }

    const patch: Record<string, unknown> = {
      description,
      is_active: isActive,
      name,
      picture: pictureUrl,
      images,
      tipo_producto: tipoProducto || undefined,
      productos_relacionados: productosRelacionados,
      rating: rating ?? undefined,
      marca: marca || undefined,
      modelo: modelo || undefined,
      variante: variante || undefined,
      condicion: condicion || undefined,
      garantia_meses: garantiaMeses ? Number(garantiaMeses) : undefined,
      precio_referencia_cop: precioReferenciaCop ? Number(precioReferenciaCop) : undefined,
      url_oficial: urlOficial || undefined,
      ...(tipoProducto === "celular" ? { specs } : {}),
    };

    if (availableQuantity.trim() !== "") {
      patch.available_quantity = availableQuantity;
    }

    // `0` is a real answer here ("we can get it same-day"), so this cannot be the truthiness check
    // the metadata fields below use — it has to be an emptiness check.
    if (leadTimeDays.trim() !== "") {
      patch.lead_time_days = Number(leadTimeDays);
    }

    if (priceNumber !== undefined && !Number.isNaN(priceNumber)) {
      patch.price = priceNumber;
    }

    if (discountNumber !== undefined && !Number.isNaN(discountNumber)) {
      patch.discount = discountNumber;
    }

    try {
      await updateItem({ id, patch }).unwrap();
      await refetch();
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.error("Update failed:", err);
      }
      // An edit used to upsert, so a stale id quietly created a stub product. It now 404s, which
      // means the row was deleted elsewhere — a different problem than a failed save.
      setErrorMsg(
        getErrorStatus(err) === HTTP_NOT_FOUND
          ? ITEM_MAINTENANCE_TEXTS.notFoundError
          : ITEM_MAINTENANCE_TEXTS.saveError,
      );
    }
  };

  if (isFetching && !item) {
    return (
      <CommonBoxStyles p={2}>
        <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
          {ITEM_MAINTENANCE_TEXTS.loading}
        </Typography>
      </CommonBoxStyles>
    );
  }

  if (isError || !item) {
    return (
      <CommonBoxStyles>
        <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
          {ITEM_MAINTENANCE_TEXTS.notFound}
        </Typography>
      </CommonBoxStyles>
    );
  }

  return (
    <CommonBoxStyles>
      <Box m={2} sx={{ background: "white" }}>
        <Box p={1} display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Typography variant="h6" fontWeight="bold" color="text.primary">
            {ITEM_MAINTENANCE_TEXTS.formTitle}
          </Typography>
          {/*
            Provenance, shown and never editable (EB-524). It sits by the title rather than among
            the fields precisely so it does not read as one: the sync owns it, and the useful
            question here is "where did this row come from?" while curating it — not "what should
            it say?".
          */}
          {fuente.length > 0 &&
            fuente.map((source) => (
              <Chip key={source} label={source} size="small" variant="outlined" />
            ))}
        </Box>
        <Box p={1}>
          <ImageUploadBar>
            <Button
              onClick={openMainPicker}
              variant={"outlined"}
              disabled={uploading}
              startIcon={<AddPhotoAlternateIcon />}
              fullWidth
              sx={{
                fontWeight: "bold",
                boxShadow: 1,
                bgcolor: "white",
              }}
            >
              {uploading
                ? ITEM_MAINTENANCE_TEXTS.uploading
                : ITEM_MAINTENANCE_TEXTS.updateMainImage}
            </Button>
            <Button
              onClick={openGalleryPicker}
              variant="outlined"
              disabled={uploading}
              startIcon={<AddPhotoAlternateIcon />}
              sx={{
                fontWeight: "bold",
                boxShadow: 1,
                bgcolor: "white",
              }}
              fullWidth
            >
              {uploading
                ? ITEM_MAINTENANCE_TEXTS.uploading
                : ITEM_MAINTENANCE_TEXTS.addGalleryImage}
            </Button>
          </ImageUploadBar>

          {errorMsg && (
            <Box mt={2}>
              <Alert severity="error">{errorMsg}</Alert>
            </Box>
          )}

          {previewUrl && (
            <Box mt={2}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {ITEM_MAINTENANCE_TEXTS.previewLabel}
              </Typography>
              <img
                src={resolveAssetUrl(previewUrl)}
                alt="Vista previa"
                style={{ maxWidth: 200, borderRadius: IMAGE_BORDER_RADIUS }}
                referrerPolicy="no-referrer"
              />
            </Box>
          )}

          <input
            ref={imageMainInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleMainSelected}
          />
          <input
            ref={imageGalleryInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleGallerySelected}
          />

          <FormFieldGrid mt={3}>
            <TextField
              label={FORM_LABELS.name}
              value={name}
              onChange={(e) => setName(e.target.value)}
              size="small"
              fullWidth
              InputLabelProps={INPUT_LABEL_PROPS}
            />
            <TextField
              label={FORM_LABELS.description}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              size="small"
              fullWidth
              multiline
              minRows={3}
              InputLabelProps={INPUT_LABEL_PROPS}
            />
          </FormFieldGrid>

          <FormFieldGridResponsive>
            <FormControl fullWidth size="small">
              <InputLabel {...INPUT_LABEL_PROPS}>{FORM_LABELS.tipoProducto}</InputLabel>
              <Select
                value={tipoProducto}
                onChange={(e) => setTipoProducto(e.target.value)}
                label={FORM_LABELS.tipoProducto}
              >
                <MenuItem value="">
                  <em>Sin categoría</em>
                </MenuItem>
                {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label={FORM_LABELS.availableQuantity}
              value={availableQuantity}
              onChange={(e) => setAvailableQuantity(e.target.value)}
              size="small"
              fullWidth
              InputLabelProps={INPUT_LABEL_PROPS}
            />

            <TextField
              label={FORM_LABELS.leadTimeDays}
              value={leadTimeDays}
              onChange={(e) => setLeadTimeDays(onlyNumbers(e.target.value))}
              size="small"
              fullWidth
              inputMode="numeric"
              helperText={FORM_LABELS.leadTimeDaysHelp}
              InputLabelProps={INPUT_LABEL_PROPS}
            />

            <TextField
              label={FORM_LABELS.discount}
              value={discount}
              onChange={(e) => setDiscount(onlyNumbers(e.target.value))}
              size="small"
              fullWidth
              inputMode="numeric"
              helperText="0-100%"
              InputLabelProps={INPUT_LABEL_PROPS}
            />

            <FormControlLabel
              control={
                <Checkbox checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              }
              label={FORM_LABELS.active}
            />

            <TextField
              label={FORM_LABELS.price}
              value={price}
              onChange={(e) => setPrice(onlyNumbers(e.target.value))}
              size="small"
              fullWidth
              inputMode="numeric"
              InputLabelProps={INPUT_LABEL_PROPS}
            />

            <Box>
              <Typography variant="body2" fontWeight="bold" color="primary.main" sx={{ mb: 0.5 }}>
                Rating
              </Typography>
              <Rating
                value={rating}
                precision={0.5}
                onChange={(_, newValue) => setRating(newValue)}
              />
            </Box>
          </FormFieldGridResponsive>

          <Box mt={3}>
            <Accordion
              defaultExpanded={
                !!(
                  marca ||
                  modelo ||
                  variante ||
                  condicion ||
                  garantiaMeses ||
                  precioReferenciaCop ||
                  urlOficial
                )
              }
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {FORM_LABELS.metadataAccordion}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormFieldGridResponsive>
                  <TextField
                    label={FORM_LABELS.marca}
                    value={marca}
                    onChange={(e) => setMarca(e.target.value)}
                    size="small"
                    fullWidth
                    InputLabelProps={INPUT_LABEL_PROPS}
                  />
                  <TextField
                    label={FORM_LABELS.modelo}
                    value={modelo}
                    onChange={(e) => setModelo(e.target.value)}
                    size="small"
                    fullWidth
                    InputLabelProps={INPUT_LABEL_PROPS}
                  />
                  <TextField
                    label={FORM_LABELS.variante}
                    value={variante}
                    onChange={(e) => setVariante(e.target.value)}
                    size="small"
                    fullWidth
                    InputLabelProps={INPUT_LABEL_PROPS}
                  />
                  <TextField
                    label={FORM_LABELS.condicion}
                    value={condicion}
                    onChange={(e) => setCondicion(e.target.value)}
                    size="small"
                    fullWidth
                    InputLabelProps={INPUT_LABEL_PROPS}
                  />
                  <TextField
                    label={FORM_LABELS.garantiaMeses}
                    value={garantiaMeses}
                    onChange={(e) => setGarantiaMeses(onlyNumbers(e.target.value))}
                    size="small"
                    fullWidth
                    inputMode="numeric"
                    InputLabelProps={INPUT_LABEL_PROPS}
                  />
                  <TextField
                    label={FORM_LABELS.precioReferenciaCop}
                    value={precioReferenciaCop}
                    onChange={(e) => setPrecioReferenciaCop(onlyNumbers(e.target.value))}
                    size="small"
                    fullWidth
                    inputMode="numeric"
                    InputLabelProps={INPUT_LABEL_PROPS}
                  />
                  <TextField
                    label={FORM_LABELS.urlOficial}
                    value={urlOficial}
                    onChange={(e) => setUrlOficial(e.target.value)}
                    size="small"
                    fullWidth
                    InputLabelProps={INPUT_LABEL_PROPS}
                  />
                </FormFieldGridResponsive>
              </AccordionDetails>
            </Accordion>
          </Box>

          {tipoProducto === "celular" && (
            <Box mt={2}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {FORM_LABELS.specsAccordion}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <CelularSpecsEditor specs={specs} onChange={setSpecs} />
                </AccordionDetails>
              </Accordion>
            </Box>
          )}

          <Box mt={3}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {ITEM_MAINTENANCE_TEXTS.imagesLabel(images.length)}
            </Typography>
            {images.length > 0 ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {images.map((url, idx) => (
                  <Chip
                    key={`${url}-${idx}`}
                    label={url}
                    onDelete={() => void removeImageAt(idx)}
                    sx={{ maxWidth: 300 }}
                  />
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {ITEM_MAINTENANCE_TEXTS.noImages}
              </Typography>
            )}
          </Box>

          <Box mt={3}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
              {FORM_LABELS.relatedProductsSection}
            </Typography>
            <RelatedProductsEditor
              relations={productosRelacionados}
              onChange={setProductosRelacionados}
            />
          </Box>

          <SaveActionBar>
            <Button
              onClick={() => void handleSave()}
              disabled={isSaving}
              variant="outlined"
              color="success"
              startIcon={<SaveIcon />}
              fullWidth
              sx={{
                fontWeight: "bold",
                boxShadow: 2,
                maxWidth: "40%",
                bgcolor: "white",
              }}
            >
              {isSaving ? ITEM_MAINTENANCE_TEXTS.saving : ITEM_MAINTENANCE_TEXTS.save}
            </Button>
          </SaveActionBar>
        </Box>
      </Box>
    </CommonBoxStyles>
  );
};

export default ItemMaintenanceForm;
